(() => {
  const q = (selector, root = document) => root?.querySelector(selector);
  const qa = (selector, root = document) => [...(root?.querySelectorAll(selector) || [])];
  const focusableSelector = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  const currency = () => window.Shopify?.currency?.active || 'USD';
  const formatMoney = cents => new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: currency() }).format(cents / 100);
  let returnFocus = null;
  let drawerReturnFocus = null;

  const trap = (event, container) => {
    if (event.key !== 'Tab') return;
    const items = qa(focusableSelector, container).filter(element => !element.hidden && element.offsetParent !== null);
    if (!items.length) return;
    if (event.shiftKey && document.activeElement === items[0]) { event.preventDefault(); items.at(-1).focus(); }
    else if (!event.shiftKey && document.activeElement === items.at(-1)) { event.preventDefault(); items[0].focus(); }
  };

  const menu = () => q('[data-isla-mobile-menu]');
  const menuToggle = () => q('[data-isla-menu-toggle]');
  const showPanel = id => {
    qa('[data-isla-menu-panel]', menu()).forEach(panel => { panel.hidden = panel.dataset.islaMenuPanel !== id; });
    requestAnimationFrame(() => q(focusableSelector, q(`[data-isla-menu-panel="${id}"]`, menu()))?.focus());
  };
  const closeMenu = () => {
    if (!menu()) return;
    menu().hidden = true;
    menuToggle()?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('isla-lock');
    showPanel('root');
    returnFocus?.focus();
  };
  const bindHeader = root => {
    const toggle = q('[data-isla-menu-toggle]', root);
    if (toggle && !toggle.dataset.islaBound) {
      toggle.dataset.islaBound = 'true';
      toggle.addEventListener('click', () => {
        returnFocus = document.activeElement;
        menu().hidden = false;
        toggle.setAttribute('aria-expanded', 'true');
        document.body.classList.add('isla-lock');
        requestAnimationFrame(() => q('[data-isla-menu-close]', menu())?.focus());
      });
    }
    const close = q('[data-isla-menu-close]', root);
    if (close && !close.dataset.islaBound) { close.dataset.islaBound = 'true'; close.addEventListener('click', closeMenu); }
    qa('[data-isla-menu-target]', root).forEach(control => {
      if (control.dataset.islaBound) return;
      control.dataset.islaBound = 'true';
      control.addEventListener('click', () => showPanel(control.dataset.islaMenuTarget));
    });
    qa('[data-isla-menu-back]', root).forEach(control => {
      if (control.dataset.islaBound) return;
      control.dataset.islaBound = 'true';
      control.addEventListener('click', () => showPanel(control.dataset.islaMenuBack));
    });
    qa('[data-isla-desktop-nav]', root).forEach(nav => {
      if (nav.dataset.islaBound) return;
      nav.dataset.islaBound = 'true';
      const control = q('[data-isla-desktop-toggle]', nav);
      const panel = q('[data-isla-desktop-panel]', nav);
      let closeTimer;
      const cancelClose = () => { if (closeTimer) window.clearTimeout(closeTimer); closeTimer = null; };
      const set = open => {
        cancelClose();
        if (open) {
          qa('[data-isla-desktop-nav]', root).forEach(otherNav => {
            if (otherNav === nav) return;
            q('[data-isla-desktop-toggle]', otherNav)?.setAttribute('aria-expanded', 'false');
            const otherPanel = q('[data-isla-desktop-panel]', otherNav);
            if (otherPanel) otherPanel.hidden = true;
          });
        }
        control?.setAttribute('aria-expanded', String(open));
        if (panel) panel.hidden = !open;
      };
      const scheduleClose = () => { cancelClose(); closeTimer = window.setTimeout(() => set(false), 700); };
      control?.addEventListener('click', () => set(control.getAttribute('aria-expanded') !== 'true'));
      nav.addEventListener('pointerenter', event => { if (event.pointerType !== 'touch') set(true); });
      nav.addEventListener('pointerleave', event => { if (event.pointerType !== 'touch') scheduleClose(); });
      panel?.addEventListener('pointerenter', cancelClose);
      nav.addEventListener('focusout', event => { if (!nav.contains(event.relatedTarget)) set(false); });
      document.addEventListener('pointerdown', event => { if (!nav.contains(event.target)) set(false); });
      document.addEventListener('keydown', event => {
        if (event.key !== 'Escape' || panel?.hidden) return;
        set(false);
        control?.focus();
      });
    });
  };

  const bindHoverImages = root => {
    qa('[data-isla-hover-src]', root).forEach(image => {
      if (image.dataset.islaBound) return;
      image.dataset.islaBound = 'true';
      const load = () => {
        image.src = image.dataset.islaHoverSrc;
        image.srcset = image.dataset.islaHoverSrcset || '';
        image.removeAttribute('data-isla-hover-src');
      };
      image.closest('article')?.addEventListener('mouseenter', load, { once: true });
      image.closest('article')?.addEventListener('focusin', load, { once: true });
    });
  };

  const bindCollection = root => {
    const toggle = q('[data-isla-filter-toggle]', root);
    const form = q('[data-isla-filter-form]', root);
    if (toggle && form && !toggle.dataset.islaBound) {
      toggle.dataset.islaBound = 'true';
      toggle.addEventListener('click', () => {
        const open = toggle.getAttribute('aria-expanded') !== 'true';
        toggle.setAttribute('aria-expanded', String(open));
        q('span', toggle).textContent = open ? '−' : '+';
        form.hidden = !open;
      });
    }
    const sort = q('[data-isla-sort]', root);
    const grid = q('.product-grid, .collection-product-grid', root);
    let items = qa('[data-isla-filter-item]', grid);
    const aliases = {
      linens: ['linen', 'napkin', 'placemat', 'tablecloth'],
      ceramics: ['ceramic', 'plate', 'bowl', 'jug'],
      accents: ['accent', 'accessor', 'ring', 'coaster', 'glass', 'tray', 'holder'],
      napkins: ['napkin'],
      placemats: ['placemat'],
      tablecloths: ['tablecloth'],
      plates: ['plate', 'bowl', 'dessert'],
      jugs: ['jug'],
      rings: ['ring'],
      coasters: ['coaster'],
      glassware: ['glass'],
      amanecer: ['amanecer'],
      atardecer: ['atardecer']
    };
    const state = { type: 'all', color: 'all', stock: false };
    const matches = (item, value) => {
      if (value === 'all') return true;
      const terms = (item.dataset.islaFilterTerms || '').toLowerCase();
      return (aliases[value] || [value]).some(term => terms.includes(term));
    };
    const applyFilters = () => {
      items.forEach(item => {
        item.hidden = !(matches(item, state.type) && matches(item, state.color) && (!state.stock || item.dataset.islaAvailable === 'true'));
      });
      const result = q('[data-result-count]', root);
      if (result) {
        const visible = items.filter(item => !item.hidden).length;
        result.textContent = `Showing ${visible} of ${result.dataset.resultTotal} ${result.dataset.resultNoun}`;
      }
    };
    qa('[data-isla-filter-value]', form).forEach(button => {
      if (button.dataset.islaBound) return;
      button.dataset.islaBound = 'true';
      button.addEventListener('click', () => {
        const group = button.dataset.islaFilterGroup;
        state[group] = button.dataset.islaFilterValue;
        qa(`[data-isla-filter-group="${group}"]`, form).forEach(option => {
          const active = option === button;
          option.classList.toggle('is-active', active);
          option.setAttribute('aria-pressed', String(active));
        });
        applyFilters();
      });
    });
    const stock = q('[data-isla-stock-filter]', form);
    if (stock && !stock.dataset.islaBound) {
      stock.dataset.islaBound = 'true';
      stock.addEventListener('change', () => {
        state.stock = stock.checked;
        applyFilters();
      });
    }
    const applySort = () => {
      if (!sort) return;
      const mode = sort.value;
      const ordered = [...items].sort((a, b) => {
        const aName = q('h3', a)?.textContent.trim() || '';
        const bName = q('h3', b)?.textContent.trim() || '';
        const aPrice = Number(a.dataset.islaPrice || 0);
        const bPrice = Number(b.dataset.islaPrice || 0);
        if (mode === 'price-ascending') return aPrice - bPrice;
        if (mode === 'price-descending') return bPrice - aPrice;
        if (mode === 'title-ascending') return aName.localeCompare(bName);
        return Number(a.dataset.islaOrder || 0) - Number(b.dataset.islaOrder || 0);
      });
      ordered.forEach(item => grid.appendChild(item));
    };
    if (sort && !sort.dataset.islaBound) {
      sort.dataset.islaBound = 'true';
      sort.addEventListener('change', applySort);
    }
    const bindImageToggles = scope => {
      qa('[data-image-toggle]', scope).forEach(button => {
        if (button.dataset.islaBound) return;
        button.dataset.islaBound = 'true';
        button.addEventListener('click', () => {
          const card = button.closest('.collection-product');
          const image = q('[data-isla-hover-src]', card);
          if (image) {
            image.src = image.dataset.islaHoverSrc;
            image.srcset = image.dataset.islaHoverSrcset || '';
            image.removeAttribute('data-isla-hover-src');
          }
          card?.classList.toggle('show-secondary');
        });
      });
    };
    bindImageToggles(root);

    const loadMore = q('[data-isla-load-more]', root);
    if (loadMore && !loadMore.dataset.islaBound) {
      loadMore.dataset.islaBound = 'true';
      loadMore.addEventListener('click', async () => {
        if (loadMore.disabled || !loadMore.dataset.nextUrl) return;
        const originalLabel = loadMore.dataset.label || loadMore.textContent.trim();
        const status = q('[data-isla-load-status]', root);
        loadMore.disabled = true;
        loadMore.textContent = 'Loading…';
        try {
          const response = await fetch(loadMore.dataset.nextUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
          if (!response.ok) throw new Error(`Unable to load products (${response.status})`);
          const page = new DOMParser().parseFromString(await response.text(), 'text/html');
          const nextGrid = q(`#${grid.id}`, page);
          if (!nextGrid) throw new Error('Unable to find the next product group.');
          const incoming = qa('[data-isla-filter-item]', nextGrid);
          const start = items.length;
          incoming.forEach((item, index) => {
            item.dataset.islaOrder = String(start + index);
            grid.appendChild(item);
          });
          items = qa('[data-isla-filter-item]', grid);
          bindHoverImages(grid);
          bindImageToggles(grid);
          applyFilters();
          applySort();
          const nextButton = q('[data-isla-load-more]', page);
          if (nextButton?.dataset.nextUrl) {
            loadMore.dataset.nextUrl = nextButton.dataset.nextUrl;
            loadMore.disabled = false;
            loadMore.textContent = originalLabel;
          } else {
            loadMore.remove();
          }
          if (status) status.textContent = `${incoming.length} more products loaded.`;
        } catch (error) {
          loadMore.disabled = false;
          loadMore.textContent = originalLabel;
          if (status) status.textContent = 'Products could not be loaded. Please try again.';
          console.error(error);
        }
      });
    }
  };

  const bindProduct = root => {
    const productRoot = q('[data-isla-product]', root);
    if (!productRoot || productRoot.dataset.islaBound) return;
    productRoot.dataset.islaBound = 'true';
    const variants = JSON.parse(q('[data-isla-variants]', productRoot)?.textContent || '[]');
    const form = q('[data-isla-product-form]', productRoot);
    const quantity = q('[data-qty]', form);
    const sync = () => {
      const chosen = qa('fieldset input[type="radio"]:checked', form).filter(input => input.name.startsWith('options[')).map(input => input.value);
      const variant = variants.find(candidate => candidate.options.every((value, index) => value === chosen[index])) || variants[0];
      if (!variant) return;
      q('[data-isla-variant-select]', form).value = variant.id;
      const add = q('[data-isla-add]', form);
      add.disabled = !variant.available;
      q('[data-isla-add-label]', form).textContent = variant.available ? `${add.dataset.addLabel} · ${formatMoney(variant.price)}` : add.dataset.soldLabel;
      q('[data-isla-price]', productRoot).innerHTML = `${variant.compare_at_price > variant.price ? `<s>${formatMoney(variant.compare_at_price)}</s>` : ''}<span>${formatMoney(variant.price)}</span>`;
      q('[data-isla-mobile-price]', productRoot).textContent = formatMoney(variant.price);
    };
    form?.addEventListener('change', sync);
    q('[data-qty-minus]', form)?.addEventListener('click', () => { quantity.value = Math.max(1, Number(quantity.value) - 1); });
    q('[data-qty-plus]', form)?.addEventListener('click', () => { quantity.value = Number(quantity.value) + 1; });
    form?.addEventListener('submit', async event => {
      if (event.submitter?.name !== 'add') return;
      event.preventDefault();
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
      if (!response.ok) { form.submit(); return; }
      const item = await response.json();
      document.dispatchEvent(new CustomEvent('cart:add', { detail: { id: item.variant_id, qty: item.quantity } }));
      await refreshDrawer(true);
    });
    q('[data-isla-mobile-add]', productRoot)?.addEventListener('click', () => q('[data-isla-add]', form)?.click());

    const gallery = q('[data-product-gallery]', productRoot);
    const slides = qa('[data-gallery-slide]', productRoot);
    const thumbs = qa('[data-gallery-thumb]', productRoot);
    const count = q('[data-gallery-count]', productRoot);
    const activate = index => {
      slides.forEach((slide, slideIndex) => slide.classList.toggle('is-active', slideIndex === index));
      thumbs.forEach((thumb, thumbIndex) => thumb.classList.toggle('is-active', thumbIndex === index));
      if (count) count.textContent = window.innerWidth <= 900 ? `${index + 1} of ${slides.length} · swipe` : `${index + 1} / ${slides.length}`;
    };
    thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => activate(index)));
    gallery?.addEventListener('scroll', () => { if (window.innerWidth <= 900) activate(Math.max(0, Math.min(slides.length - 1, Math.round(gallery.scrollLeft / gallery.clientWidth)))); }, { passive: true });
    const lightbox = q('[data-product-lightbox]', productRoot);
    qa('[data-gallery-zoom]', productRoot).forEach(button => button.addEventListener('click', () => {
      const source = q('img', button);
      const target = q('img', lightbox);
      target.src = source.currentSrc || source.src;
      target.alt = source.alt;
      lightbox.showModal();
    }));
    q('[data-lightbox-close]', lightbox)?.addEventListener('click', () => lightbox.close());

    const mobileBar = q('[data-mobile-purchase]', productRoot);
    const primaryAdd = q('[data-isla-add]', form);
    const purchaseBoundary = q('.newsletter, .footer', document);
    const updateBar = () => {
      const reachedFooter = purchaseBoundary && purchaseBoundary.getBoundingClientRect().top <= window.innerHeight * .5;
      const passedPrimary = primaryAdd && primaryAdd.getBoundingClientRect().bottom < 0;
      mobileBar?.classList.toggle('is-visible', window.innerWidth <= 700 && passedPrimary && !reachedFooter);
    };
    window.addEventListener('scroll', updateBar, { passive: true });
    window.addEventListener('resize', updateBar);
    updateBar();
  };

  const drawer = () => q('[data-isla-cart-drawer]');
  const overlay = () => q('[data-isla-cart-overlay]');
  const closeDrawer = () => {
    if (!drawer()) return;
    drawer().hidden = true;
    if (overlay()) overlay().hidden = true;
    document.body.classList.remove('isla-lock');
    drawerReturnFocus?.focus();
  };
  const bindDrawer = root => {
    q('[data-isla-cart-close]', root)?.addEventListener('click', closeDrawer, { once: true });
    qa('[data-isla-line-change]', root).forEach(button => {
      if (button.dataset.islaBound) return;
      button.dataset.islaBound = 'true';
      button.addEventListener('click', async () => {
        await fetch(`${window.Shopify.routes.root}cart/change.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ line: Number(button.dataset.islaLineChange), quantity: Number(button.dataset.quantity) }) });
        await refreshDrawer(true);
      });
    });
  };
  const openDrawer = trigger => {
    if (!drawer()) { window.location.href = window.Shopify.routes.root + 'cart'; return; }
    drawerReturnFocus = trigger;
    drawer().hidden = false;
    if (overlay()) overlay().hidden = false;
    document.body.classList.add('isla-lock');
    q('[data-isla-cart-close]', drawer())?.focus();
  };
  const refreshDrawer = async open => {
    const current = drawer();
    if (!current) return;
    const id = current.dataset.sectionId;
    const response = await fetch(`${window.Shopify.routes.root}?sections=${id}`);
    const sections = await response.json();
    const doc = new DOMParser().parseFromString(sections[id], 'text/html');
    const nextDrawer = q('[data-isla-cart-drawer]', doc);
    const nextOverlay = q('[data-isla-cart-overlay]', doc);
    current.replaceWith(nextDrawer);
    overlay()?.replaceWith(nextOverlay);
    bindDrawer(document);
    const cart = await fetch(`${window.Shopify.routes.root}cart.js`).then(result => result.json());
    qa('[data-isla-cart-count]').forEach(element => { element.textContent = cart.item_count; });
    qa('[data-isla-cart-count-wrap]').forEach(element => { element.hidden = cart.item_count === 0; });
    if (open) openDrawer(drawerReturnFocus);
  };

  const bindCartPage = root => {
    qa('[data-isla-cart-quantity]', root).forEach(button => {
      if (button.dataset.islaBound) return;
      button.dataset.islaBound = 'true';
      button.addEventListener('click', () => {
        const holder = button.closest('.cart-quantity');
        const input = q('[data-isla-cart-input]', holder);
        const next = Math.max(0, Number(input.value) + (button.dataset.islaCartQuantity === 'plus' ? 1 : -1));
        input.value = next;
        q('output', holder).textContent = next;
        q('#IslaCartForm', root)?.requestSubmit(q('[name="update"]', root));
      });
    });
    const track = q('[data-rec-track]', root);
    q('[data-rec-next]', root)?.addEventListener('click', () => track?.scrollBy({ left: track.clientWidth * .8, behavior: 'smooth' }), { once: true });
    q('[data-rec-prev]', root)?.addEventListener('click', () => track?.scrollBy({ left: -track.clientWidth * .8, behavior: 'smooth' }), { once: true });
  };

  const bindAll = root => {
    bindHeader(root);
    bindHoverImages(root);
    bindCollection(root);
    bindProduct(root);
    bindDrawer(root);
    bindCartPage(root);
    qa('[data-isla-cart-open]', root).forEach(button => {
      if (button.dataset.islaBound) return;
      button.dataset.islaBound = 'true';
      button.addEventListener('click', () => openDrawer(button));
    });
    const backdrop = q('[data-isla-cart-overlay]', root);
    if (backdrop && !backdrop.dataset.islaBound) { backdrop.dataset.islaBound = 'true'; backdrop.addEventListener('click', closeDrawer); }
  };
  bindAll(document);
  document.addEventListener('keydown', event => {
    if (menu() && !menu().hidden) { if (event.key === 'Escape') closeMenu(); else trap(event, menu()); }
    else if (drawer() && !drawer().hidden) { if (event.key === 'Escape') closeDrawer(); else trap(event, drawer()); }
  });
  document.addEventListener('shopify:section:load', event => bindAll(event.target));
})();
